import { useEffect } from "react";
import { Link } from "react-router-dom";
import NavBar from "@/components/site/NavBar";
import Footer from "@/components/site/Footer";

const SECTIONS = [
  {
    id: "acceptance",
    title: "Acceptance of Terms",
    body: (
      <p>
        Welcome to the website of Jos Family Law (the &ldquo;Firm,&rdquo;
        &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). These Terms
        of Use (&ldquo;Terms&rdquo;) govern your access to and use of{" "}
        <a
          href="https://www.josfamilylaw.com"
          target="_blank"
          rel="noreferrer"
          className="text-teal hover:text-ink transition-colors underline"
        >
          www.josfamilylaw.com
        </a>{" "}
        and any related landing pages (together, the &ldquo;Site&rdquo;). By
        accessing or using the Site, you agree to be bound by these Terms. If
        you do not agree, please do not use the Site.
      </p>
    ),
  },
  {
    id: "no-attorney-client",
    title: "No Attorney-Client Relationship",
    body: (
      <p>
        The Site is intended to provide general information about the Firm and
        its services. Use of the Site, including submitting a contact form,
        calling our office, or communicating with our automated chat or phone
        assistant, does not create an attorney-client relationship between
        you and Jos Family Law or any of its attorneys. An attorney-client
        relationship is formed only when the Firm has agreed to represent you
        and a written engagement agreement has been signed.
      </p>
    ),
  },
  {
    id: "no-legal-advice",
    title: "No Legal Advice",
    body: (
      <p>
        The content on the Site, including any blog posts, FAQs, or general
        descriptions of legal topics, is provided for general informational
        purposes only and does not constitute legal advice. Laws change
        frequently and vary by jurisdiction, and the information on the Site
        may not reflect the most current legal developments. You should not
        act, or refrain from acting, based on any information on the Site
        without seeking advice from a qualified attorney regarding your
        specific situation.
      </p>
    ),
  },
  {
    id: "advertising-disclaimer",
    title: "Advertising Disclaimer",
    body: (
      <p>
        Portions of the Site, including landing pages used in connection with
        our advertising campaigns, may constitute attorney advertising under
        the California Rules of Professional Conduct. Any case results,
        testimonials, or descriptions of past outcomes referenced on the Site
        are provided for illustrative purposes only. Past results do not
        guarantee or predict a similar outcome in any future matter, as every
        case depends on its own facts and circumstances.
      </p>
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable Use",
    body: (
      <>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Site for any unlawful purpose or in violation of these Terms</li>
          <li>
            Attempt to gain unauthorized access to the Site, its servers, or
            any connected systems
          </li>
          <li>
            Interfere with the proper functioning of the Site, including
            through the introduction of viruses or other harmful code
          </li>
          <li>
            Use any automated means, such as bots or scrapers, to access the
            Site without our prior written consent
          </li>
          <li>
            Submit false, misleading, or fraudulent information through any
            form, chat, or call
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "electronic-communications",
    title: "Electronic Communications",
    body: (
      <p>
        Communications sent through the Site, including contact forms, chat,
        and email, are not necessarily secure or confidential, and the Firm
        cannot guarantee that information transmitted over the internet will
        not be intercepted, lost, or accessed by unauthorized parties. Please
        do not send sensitive or confidential information about your legal
        matter through these channels until you have an established
        attorney-client relationship with the Firm.
      </p>
    ),
  },
  {
    id: "sms-terms",
    title: "SMS / Text Messaging Terms",
    body: (
      <>
        <p>
          By providing your mobile number to Jos Family Law, Inc. and
          agreeing to be contacted, you consent to receive text (SMS)
          messages from us about your inquiry or matter, including intake
          form links, appointment and consultation reminders, case-related
          updates, and replies to your questions.
        </p>
        <p>
          Message frequency varies depending on your interaction with us
          and the status of your matter. Message and data rates may apply;
          these are charged by your mobile carrier, and Jos Family Law does
          not charge for the messages.
        </p>
        <p>
          You may opt out at any time by replying{" "}
          <strong>STOP</strong> to any message, after which we will send a
          single confirmation and no further texts unless you opt back in.
          For help, contact us at{" "}
          <a
            href="tel:+17147337066"
            className="text-teal hover:text-ink transition-colors font-medium"
          >
            714-733-7066
          </a>
          .
        </p>
        <p>
          Mobile carriers (including AT&amp;T, T-Mobile, and Verizon) are
          not liable for delayed or undelivered messages, and delivery is
          not guaranteed.
        </p>
        <p>
          Your information is handled in accordance with our{" "}
          <Link
            to="/privacy-policy"
            className="text-teal hover:text-ink transition-colors underline"
          >
            Privacy Policy
          </Link>
          ; we do not share mobile information or opt-in consent with third
          parties or affiliates for marketing or promotional purposes.
        </p>
      </>
    ),
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property",
    body: (
      <p>
        All content on the Site, including text, graphics, logos, and the
        JOS. mark, is the property of Jos Family Law or its licensors and is
        protected by copyright, trademark, and other intellectual property
        laws. You may view and print content from the Site for your personal,
        non-commercial use, but may not reproduce, distribute, or create
        derivative works from any content without our prior written
        permission.
      </p>
    ),
  },
  {
    id: "third-party-links",
    title: "Third-Party Links and Services",
    body: (
      <p>
        The Site may contain links to third-party websites or integrate with
        third-party services, such as advertising platforms, scheduling tools,
        or chat services. These third parties are not controlled by the Firm,
        and we are not responsible for their content, policies, or practices.
        Your use of any third-party site or service is at your own risk and
        subject to that party&rsquo;s own terms.
      </p>
    ),
  },
  {
    id: "disclaimer-warranties",
    title: "Disclaimer of Warranties",
    body: (
      <p>
        The Site is provided &ldquo;as is&rdquo; and &ldquo;as
        available&rdquo; without warranties of any kind, whether express or
        implied, including warranties of merchantability, fitness for a
        particular purpose, or non-infringement. We do not warrant that the
        Site will be uninterrupted, error-free, or free of viruses or other
        harmful components.
      </p>
    ),
  },
  {
    id: "limitation-liability",
    title: "Limitation of Liability",
    body: (
      <p>
        To the fullest extent permitted by law, Jos Family Law and its
        attorneys, employees, and agents shall not be liable for any indirect,
        incidental, special, consequential, or punitive damages arising out of
        or related to your use of, or inability to use, the Site.
      </p>
    ),
  },
  {
    id: "indemnification",
    title: "Indemnification",
    body: (
      <p>
        You agree to indemnify, defend, and hold harmless Jos Family Law and
        its attorneys, employees, and agents from and against any claims,
        losses, damages, liabilities, and expenses, including reasonable
        attorneys&rsquo; fees, arising out of or related to your violation of
        these Terms, your misuse of the Site, or any content or information
        you submit through the Site.
      </p>
    ),
  },
  {
    id: "general-provisions",
    title: "General Provisions",
    body: (
      <p>
        If any provision of these Terms is found to be invalid or
        unenforceable, the remaining provisions will remain in full force and
        effect. Our failure to enforce any provision of these Terms shall not
        be considered a waiver of that provision or any other provision. You
        may not assign or transfer your rights under these Terms without our
        prior written consent. These Terms, together with our{" "}
        <Link
          to="/privacy-policy"
          className="text-teal hover:text-ink transition-colors underline"
        >
          Privacy Policy
        </Link>
        , constitute the entire agreement between you and the Firm regarding
        your use of the Site.
      </p>
    ),
  },
  {
    id: "governing-law",
    title: "Governing Law",
    body: (
      <p>
        These Terms are governed by the laws of the State of California,
        without regard to its conflict of law provisions. Any dispute arising
        from these Terms or your use of the Site shall be subject to the
        exclusive jurisdiction of the state and federal courts located in
        Orange County, California.
      </p>
    ),
  },
  {
    id: "changes",
    title: "Changes to These Terms",
    body: (
      <p>
        We may revise these Terms at any time by updating this page. The
        &ldquo;Effective Date&rdquo; above reflects the date of the most
        recent revision. Your continued use of the Site after changes are
        posted constitutes your acceptance of the revised Terms.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact Us",
    body: (
      <>
        <p>If you have questions about these Terms, please contact us:</p>
        <div className="not-prose mt-6 border-l-2 border-teal pl-5 font-body text-[15px] leading-[1.8] text-ink">
          <p className="font-display italic text-2xl text-ink mb-2">
            Jos Family Law
          </p>
          <p>1918 W. Chapman Avenue, Suite 200, Orange, CA 92868</p>
          <p>
            Phone:{" "}
            <a
              href="tel:+17147337066"
              className="text-teal hover:text-ink transition-colors font-medium"
            >
              714-733-7066
            </a>
          </p>
        </div>
      </>
    ),
  },
];

const LAST_UPDATED = "December 15, 2025";

export default function TermsOfUse() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="terms-page" className="relative bg-paper text-ink">
      <NavBar />

      {/* Hero */}
      <section className="bg-paper pt-16 md:pt-24 pb-12 md:pb-16 border-b border-gray-200/60">
        <div className="max-w-[920px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex items-center gap-4 mb-8">
            <span className="w-10 h-px bg-teal" />
            <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-gray-600">
              Terms of Use
            </p>
          </div>
          <h1 className="font-display text-[44px] md:text-[64px] lg:text-[76px] leading-[1.02] tracking-tight text-ink">
            The rules for{" "}
            <span className="italic">using this site.</span>
          </h1>
          <p className="mt-6 font-mono text-[11px] tracking-[0.24em] uppercase text-gray-600">
            Last Updated · {LAST_UPDATED}
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-24">
        <div className="max-w-[920px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="space-y-16">
            {SECTIONS.map((s) => (
              <article
                key={s.id}
                id={s.id}
                data-testid={`terms-section-${s.id}`}
                className="grid grid-cols-12 gap-6 md:gap-10 scroll-mt-24"
              >
                <div className="col-span-12 md:col-span-3">
                  <h2 className="font-display text-2xl md:text-[28px] leading-tight tracking-tight text-ink">
                    {s.title}
                  </h2>
                </div>
                <div
                  className="col-span-12 md:col-span-9 space-y-4 text-[16px] leading-[1.8] text-gray-800
                  [&_p]:text-[16px] [&_p]:leading-[1.8] [&_p]:text-gray-800
                  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-3 [&_ul]:marker:text-teal
                  [&_li]:text-[16px] [&_li]:leading-[1.75]
                  [&_strong]:text-ink [&_strong]:font-semibold"
                >
                  {s.body}
                </div>
              </article>
            ))}
          </div>

          {/* Back link */}
          <div className="mt-20 pt-10 border-t border-gray-200/60 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <Link
              to="/child-custody-attorney-orange-county"
              data-testid="terms-back-home"
              className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-ink hover:text-teal transition-colors"
            >
              <span>← Back to Home</span>
            </Link>
            <a
              href="/child-custody-attorney-orange-county#consultation"
              className="inline-flex items-center justify-center bg-teal text-ink hover:bg-ink hover:text-paper transition-colors duration-300 px-8 py-4"
            >
              <span className="font-mono text-[12px] tracking-[0.24em] uppercase font-semibold">
                Schedule Your Confidential Consultation
              </span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
